import BaseSerializer from './Base';
import Vector from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import {asArray} from 'ol/color';
import {Icon, Circle} from 'ol/style';
import {fromCircle} from 'ol/geom/Polygon';
import {isObject, isDefined, isEmpty, isArray, genId} from '@config/utils';

class VectorSerializer extends BaseSerializer {
    constructor() {
        super();
        this.sourceCls = Vector;
         /**
         * The types of styles that mapfish supports.
         *
         * @private
         */
        this.PRINTSTYLE_TYPES = {
            POINT: 'Point',
            LINE_STRING: 'LineString',
            POLYGON: 'Polygon'
        };

        /**
         * An object that maps an `ol.geom.GeometryType` to a #PRINTSTYLE_TYPES.
         *
         * @private
         */
        this.GEOMETRY_TYPE_TO_PRINTSTYLE_TYPE = {}; // filled once class is defined

        /**
         * A fallback serialization of a vector layer that will be used if
         * the given source e.g. doesn't have any features.
         *
         * @private
         */
        this.FALLBACK_SERIALIZATION = {
            geoJson: {
                type: 'FeatureCollection',
                features: []
            },
            opacity: 1,
            style: {
                'version': '2',
                '*': {
                    symbolizers: [{
                        type: 'point',
                        strokeColor: 'white',
                        strokeOpacity: 1,
                        strokeWidth: 4,
                        strokeDashstyle: 'solid',
                        fillColor: 'red'
                    }]
                }
            },
            type: 'geojson'
        }

            /**
         * The prefix we will give to the generated styles. Every feature will
         * -- once it is serialized -- have a property constructed with
         * the #FEAT_STYLE_PREFIX and a counter. For every unique combination
         * of #FEAT_STYLE_PREFIX  + i with the value style uid (see #getUid
         * and #GX_UID_PROPERTY), the layer serialization will also have a
         * CQL entry with a matching symbolizer:
         *
         *     {
         *          // …
         *          style: {
         *              "[_gx3_style_0='ext-46']": {
         *                  symbolizer: {
         *                      // …
         *                  }
         *              }
         *          },
         *          geoJson: {
         *              // …
         *              features: [
         *                  {
         *                      // …
         *                      properties: {
         *                          '_gx3_style_0': 'ext-46'
         *                          // …
         *                      }
         *                  }
         *              ]
         *          }
         *          // …
         *     }
         *
         * @private
         */
        this.FEAT_STYLE_PREFIX = '_cityhub_style_';

        /**
         * The name / identifier for the uid property that is assigned and read
         * out in #getUid
         *
         * @private
         */
        this.GX_UID_PROPERTY = '__cityhub_uid__';

        /**
         * A shareable instance of ol.format.GeoJSON to serialize the features.
         *
         * @private
         */
        this.format = new GeoJSON();

        // This is ol.geom.GeometryType, from
        // https://github.com/openlayers/ol3/blob/master/src/ol/geom/geometry.js
        let olGeomTypes = {
            POINT: 'Point',
            LINE_STRING: 'LineString',
            LINEAR_RING: 'LinearRing',
            POLYGON: 'Polygon',
            MULTI_POINT: 'MultiPoint',
            MULTI_LINE_STRING: 'MultiLineString',
            MULTI_POLYGON: 'MultiPolygon',
            GEOMETRY_COLLECTION: 'GeometryCollection',
            CIRCLE: 'Circle'
        };
        // The supported types for the print
        let printStyleTypes = this.PRINTSTYLE_TYPES;

        // a map that connect ol geometry types to their mapfish equivalent;
        // Please note that not all ol geometry types can be serialized.
        var geom2print = {};
        geom2print[olGeomTypes.POINT] = printStyleTypes.POINT;
        geom2print[olGeomTypes.MULTI_POINT] = printStyleTypes.POINT;
        geom2print[olGeomTypes.LINE_STRING] = printStyleTypes.LINE_STRING;
        geom2print[olGeomTypes.MULTI_LINE_STRING] = printStyleTypes.LINE_STRING;
        geom2print[olGeomTypes.POLYGON] = printStyleTypes.POLYGON;
        geom2print[olGeomTypes.MULTI_POLYGON] = printStyleTypes.POLYGON;
        geom2print[olGeomTypes.CIRCLE] = printStyleTypes.POLYGON;

        this.GEOMETRY_TYPE_TO_PRINTSTYLE_TYPE = geom2print;
    }

    serialize(layer, source, viewRes, map) {
        var me = this;
        me.validateSource(source);
        var extent;

        if (map) {
            extent = map.getView().calculateExtent();
        }
        var format = me.format;
        var geoJsonFeatures = [];
        var mapfishStyleObject = {
            version: 2
        };

        var processFeatures = (feature) => {
            var geometry = feature.getGeometry();
            if (isEmpty(geometry)) {
                // no need to encode features with no geometry
                return;
            }
            var geometryType = geometry.getType();
            // Test Circle Geometry
            if(geometryType.toLowerCase() == 'circle') {
                let _polygonGeom = fromCircle(geometry);
                feature = feature.clone();
                feature.setGeometry(_polygonGeom);
                feature.set(this.getGeomProperty(), feature.getGeometry().getType().toLowerCase());
            }

            var geojsonFeature = format.writeFeatureObject(feature);

            // remove parent feature references as they break serialization
            // later on
            if (geojsonFeature.properties &&
                    geojsonFeature.properties.parentFeature) {
                geojsonFeature.properties.parentFeature = undefined;
            }

            var styles = null;
            var styleFunction = feature.getStyleFunction();
            if (isDefined(styleFunction)) {
                styles = styleFunction.call(feature, viewRes);
            } else {
                styleFunction = layer.getStyleFunction();
                if (isDefined(styleFunction)) {
                    styles = styleFunction.call(layer, feature, viewRes);
                }
            }

            if (!isArray(styles)) {
                styles = [styles];
            }
            if (!isEmpty(styles)) {
                geoJsonFeatures.push(geojsonFeature);
                if (isEmpty(geojsonFeature.properties)) {
                    geojsonFeature.properties = {};
                }
                styles.forEach( (style, j) => {
                    var styleId = me.getUid(style, geometryType);
                    var featureStyleProp = me.FEAT_STYLE_PREFIX + j;
                    me.encodeVectorStyle(
                        mapfishStyleObject,
                        geometryType,
                        style,
                        styleId,
                        featureStyleProp
                    );
                    geojsonFeature.properties[featureStyleProp] = styleId;
                });
            }
        };
        if (extent) {
            source.forEachFeatureInExtent(extent, processFeatures);
        } else {
            source.getFeatures().forEach(feature => processFeatures);
        }

        var serialized;

        // MapFish Print fails if there are no style rules, even if there
        // are no features either. To work around this, we add a basic
        // style in the else clause array of GeoJSON features is empty.
        if (geoJsonFeatures.length > 0) {
            var geojsonFeatureCollection = {
                type: 'FeatureCollection',
                features: geoJsonFeatures
            };
            serialized = {
                geoJson: geojsonFeatureCollection,
                opacity: layer.getOpacity(),
                style: mapfishStyleObject,
                type: 'geojson'
            };
        } else {
            serialized = this.FALLBACK_SERIALIZATION;
        }

        return serialized;
    }

    /**
     * Encodes an ol.style.Style into the passed MapFish style object.
     *
     * @param {Object} object The MapFish style object.
     * @param {ol.geom.GeometryType} geometryType The type of the GeoJSON
     *    geometry
     * @param {ol.style.Style} style The style to encode.
     * @param {String} styleId The id of the style.
     * @param {String} featureStyleProp Feature style property name.
     * @private
     */
    encodeVectorStyle(object, geometryType, style, styleId, featureStyleProp) {
        var me = this;
        var printTypes = me.PRINTSTYLE_TYPES;
        var printStyleLookup = me.GEOMETRY_TYPE_TO_PRINTSTYLE_TYPE;
        if (!isDefined(printStyleLookup[geometryType])) {
            // unsupported geometry type
            return;
        }
        var styleType = printStyleLookup[geometryType];
        var key = '[' + featureStyleProp + ' = \'' + styleId + '\']';
        if (isDefined(object[key])) {
            // do nothing if we already have a style object for this CQL
            // rule
            return;
        }
        var styleObject = {
            symbolizers: []
        };

        object[key] = styleObject;

        var fillStyle = style.getFill();
        var imageStyle = style.getImage();
        var strokeStyle = style.getStroke();
        var textStyle = style.getText();

        var hasFillStyle = !isEmpty(fillStyle);
        var hasImageStyle = !isEmpty(imageStyle);
        var hasStrokeStyle = !isEmpty(strokeStyle);
        var hasTextStyle = !isEmpty(textStyle);

        var POLYTYPE = printTypes.POLYGON;
        var LINETYPE = printTypes.LINE_STRING;
        var POINTTYPE = printTypes.POINT;
        if (styleType === POLYTYPE && hasFillStyle) {
            me.encodeVectorStylePolygon(
                styleObject.symbolizers, fillStyle, strokeStyle
            );
        } else if (styleType === LINETYPE && hasStrokeStyle) {
            me.encodeVectorStyleLine(
                styleObject.symbolizers, strokeStyle
            );
        } else if (styleType === POINTTYPE && hasImageStyle) {
            me.encodeVectorStylePoint(
                styleObject.symbolizers, imageStyle
            );
        }
        // this can be there regardless of type
        if (hasTextStyle) {
            me.encodeTextStyle(styleObject.symbolizers, textStyle);
        }
    }

    /**
     * Encodes an `ol.style.Fill` and an optional `ol.style.Stroke` and adds
     * it to the passed symbolizers array.
     *
     * @param {Object[]} symbolizers Array of MapFish Print symbolizers.
     * @param {ol.style.Fill} fillStyle Fill style.
     * @param {ol.style.Stroke} strokeStyle Stroke style. May be null.
     * @private
     */
    encodeVectorStylePolygon(symbolizers, fillStyle,
        strokeStyle) {
        var symbolizer = {
            type: 'polygon'
        };
        this.encodeVectorStyleFill(symbolizer, fillStyle);
        if (strokeStyle !== null) {
            this.encodeVectorStyleStroke(symbolizer, strokeStyle);
        }
        symbolizers.push(symbolizer);
    }

    /**
     * Encodes an `ol.style.Stroke` and adds it to the passed symbolizers
     * array.
     *
     * @param {Object[]} symbolizers Array of MapFish Print symbolizers.
     * @param {ol.style.Stroke} strokeStyle Stroke style.
     * @private
     */
    encodeVectorStyleLine(symbolizers, strokeStyle) {
        var symbolizer = {
            type: 'line'
        };
        this.encodeVectorStyleStroke(symbolizer, strokeStyle);
        symbolizers.push(symbolizer);
    }

    /**
     * Encodes an `ol.style.Image` and adds it to the passed symbolizers
     * array.
     *
     * @param {Object[]} symbolizers Array of MapFish Print symbolizers.
     * @param {ol.style.Image} imageStyle Image style.
     * @private
     */
    encodeVectorStylePoint(symbolizers, imageStyle) {
        var symbolizer;
        if (imageStyle instanceof Circle) {
            symbolizer = {
                type: 'point'
            };
            symbolizer.pointRadius = imageStyle.getRadius();
            var fillStyle = imageStyle.getFill();
            if (fillStyle !== null) {
                this.encodeVectorStyleFill(symbolizer, fillStyle);
            }
            var strokeStyle = imageStyle.getStroke();
            if (strokeStyle !== null) {
                this.encodeVectorStyleStroke(symbolizer, strokeStyle);
            }
        } else if (imageStyle instanceof Icon) {
            var src = imageStyle.getSrc();
            if (isDefined(src)) {
                var img = imageStyle.getImage();
                var canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                canvas.getContext('2d').drawImage(img, 0, 0);
                var format = 'image/' + src.match(/\.(\w+)$/)[1];
                symbolizer = {
                    type: 'point',
                    externalGraphic: canvas.toDataURL(),
                    graphicFormat: format
                };
                var rotation = imageStyle.getRotation();
                if (rotation !== 0) {
                    var degreesRotation = rotation * 180 / Math.PI;
                    symbolizer.rotation = degreesRotation;
                }
            }
        }
        if (isDefined(symbolizer)) {
            symbolizers.push(symbolizer);
        }
    }

    /**
     * Encodes an `ol.style.Text` and adds it to the passed symbolizers
     * array.
     *
     * @param {Object[]} symbolizers Array of MapFish Print symbolizers.
     * @param {ol.style.Text} textStyle Text style.
     * @private
     */
    encodeTextStyle(symbolizers, textStyle) {
        var symbolizer = {
            type: 'Text'
        };
        var label = textStyle.getText();
        if (!isDefined(label)) {
            // do not encode undefined labels
            return;
        }

        symbolizer.label = label;

        var labelAlign = textStyle.getTextAlign();
        if (isDefined(labelAlign)) {
            let align = '';
            switch(labelAlign.toLowerCase()) {
                case 'left':
                    align = 'l';
                    break;
                case 'center':
                    align = 'c';
                    break;
                case 'right':
                    align = 'r';
                    break;
                default:
                    align = labelAlign;
            }
            symbolizer.labelAlign = align;
        }

        var labelBaseline = textStyle.getTextBaseline() || 'middle';
        if (isDefined(labelBaseline) && symbolizer.labelAlign) {
            let align = '';
            switch(labelBaseline.toLowerCase()) {
                case 'bottom':
                    align = 'b';
                    break;
                case 'middle':
                    align = 'm';
                    break;
                case 'top':
                    align = 't';
                    break;
                default:
                    align = labelBaseline;
            }
            symbolizer.labelAlign = symbolizer.labelAlign + align;
        }

        var labelRotation = textStyle.getRotation();
        if (isDefined(labelRotation)) {
            // Mapfish Print expects a string to rotate text
            var strRotationDeg = (labelRotation * 180 / Math.PI) + '';
            symbolizer.labelRotation = strRotationDeg;
        }

        var offsetX = textStyle.getOffsetX();
        var offsetY = textStyle.getOffsetY();
        if (offsetX) {
            symbolizer.labelXOffset = offsetX;
        }
        if (offsetY) {
            symbolizer.labelYOffset = -offsetY;
        }

        var fontStyle = textStyle.getFont();
        if (isDefined(fontStyle)) {
            var font = fontStyle.split(' ');
            if (font.length >= 3) {
                symbolizer.fontWeight = font[0];
                symbolizer.fontSize = font[1];
                symbolizer.fontFamily = font.splice(2).join(' ');
            }
        }

        var strokeStyle = textStyle.getStroke();
        if (strokeStyle !== null) {
            var strokeColor = strokeStyle.getColor();
            var strokeColorRgba = asArray(strokeColor);
            symbolizer.haloColor = this.rgbArrayToHex(strokeColorRgba);
            symbolizer.haloOpacity = strokeColorRgba[3];
            var width = strokeStyle.getWidth();
            if (isDefined(width)) {
                symbolizer.haloRadius = width;
            }
        }

        var fillStyle = textStyle.getFill();
        if (fillStyle !== null) {
            var fillColorRgba = asArray(fillStyle.getColor());
            symbolizer.fontColor = this.rgbArrayToHex(fillColorRgba);
        }

        // Mapfish Print allows offset only if labelAlign is defined.
        if (isDefined(symbolizer.labelAlign)) {
            symbolizer.labelXOffset = textStyle.getOffsetX();
            // Mapfish uses the opposite direction of OpenLayers for y
            // axis, so the minus sign is required for the y offset to
            // be identical.
            symbolizer.labelYOffset = -textStyle.getOffsetY();
        }

        symbolizers.push(symbolizer);
    }

    /**
     * Encode the passed `ol.style.Fill` into the passed symbolizer.
     *
     * @param {Object} symbolizer MapFish Print symbolizer.
     * @param {ol.style.Fill} fillStyle Fill style.
     * @private
     */
    encodeVectorStyleFill(symbolizer, fillStyle) {
        var fillColor = fillStyle.getColor();
        if (fillColor !== null) {
            var fillColorRgba = asArray(fillColor);
            symbolizer.fillColor = this.rgbArrayToHex(fillColorRgba);
            symbolizer.fillOpacity = fillColorRgba[3];
        }
    }

    /**
     * Encode the passed `ol.style.Stroke` into the passed symbolizer.
     *
     * @param {Object} symbolizer MapFish Print symbolizer.
     * @param {ol.style.Stroke} strokeStyle Stroke style.
     * @private
     */
    encodeVectorStyleStroke(symbolizer, strokeStyle) {
        var strokeColor = strokeStyle.getColor();
        if (strokeColor !== null) {
            var strokeColorRgba = asArray(strokeColor);
            symbolizer.strokeColor = this.rgbArrayToHex(strokeColorRgba);
            symbolizer.strokeOpacity = strokeColorRgba[3];
        }
        var strokeWidth = strokeStyle.getWidth();
        if (isDefined(strokeWidth)) {
            symbolizer.strokeWidth = strokeWidth;
        }
    }

    /**
     * Takes a hex value and prepends a zero if it's a single digit.
     * Taken from https://github.com/google/closure-library color.js-file.
     * It is called `prependZeroIfNecessaryHelper` there.
     *
     * @param {String} hex Hex value to prepend if single digit.
     * @return {String} The hex value prepended with zero if it was single
     *     digit, otherwise the same value that was passed in.
     * @private
     */
    padHexValue(hex) {
        return hex.length === 1 ? '0' + hex : hex;
    }

    /**
     * Converts a color from RGB to hex representation.
     * Taken from https://github.com/google/closure-library color.js-file.
     *
     * @param {Number} r Amount of red, int between 0 and 255.
     * @param {Number} g Amount of green, int between 0 and 255.
     * @param {Number} b Amount of blue, int between 0 and 255.
     * @return {String} The passed color in hex representation.
     * @private
     */
    rgbToHex(r, g, b) {
        r = Number(r);
        g = Number(g);
        b = Number(b);
        if (isNaN(r) || r < 0 || r > 255 ||
            isNaN(g) || g < 0 || g > 255 ||
            isNaN(b) || b < 0 || b > 255) {
            this.raiseException('"(' + r + ',' + g + ',' + b + '") is not a valid ' +
                ' RGB color');
        }
        var hexR = this.padHexValue(r.toString(16));
        var hexG = this.padHexValue(g.toString(16));
        var hexB = this.padHexValue(b.toString(16));
        return '#' + hexR + hexG + hexB;
    }

    /**
     * Converts a color from RGB to hex representation.
     * Taken from https://github.com/google/closure-library color.js-file
     *
     * @param {Number[]} rgbArr An array with three numbers representing
     *    red, green and blue.
     * @return {String} The passed color in hex representation.
     * @private
     */
    rgbArrayToHex(rgbArr) {
        return this.rgbToHex(rgbArr[0], rgbArr[1], rgbArr[2]);
    }

    /**
     * Returns a unique id for this object. The object is assigned a new
     * property #GX_UID_PROPERTY and modified in place if this hasn't
     * happened in a previous call.
     *
     * @param {Object} obj The object to get the uid of.
     * @param {String} geometryType The geometryType for the style.
     * @return {String} The uid of the object.
     * @private
     */
    getUid(obj, geometryType) {
        if (!isObject(obj)) {
            this.raiseException('Cannot get uid of non-object.');
        }
        var key = this.GX_UID_PROPERTY;
        if (geometryType) {
            key += '-' + geometryType;
        }
        if (!isDefined(obj[key])) {
            obj[key] = genId();
        }
        return obj[key];
    }
};

export default VectorSerializer;